<template>
  <div class="image-browser-container">
    <div class="image-browser">
      <h1 class="green-heading">{{ $t('imageBrowser.heading') }}</h1>
      <p class="subtitle">{{ $t('imageBrowser.subtitle') }}</p>

      <p v-if="errorMessage" class="banner banner-error" role="alert">{{ errorMessage }}</p>

      <div class="filters">
        <div class="filter-field">
          <label :for="ids.category">{{ $t('imageBrowser.filters.categoryLabel') }}</label>
          <select :id="ids.category" v-model="filters.category">
            <option value="">{{ $t('imageBrowser.filters.categoryAll') }}</option>
            <option v-for="cat in categoryIds" :key="cat" :value="cat">
              {{ $t(`common.categories.${cat}`) }}
            </option>
          </select>
        </div>

        <div class="filter-field">
          <label :for="ids.status">{{ $t('imageBrowser.filters.statusLabel') }}</label>
          <select :id="ids.status" v-model="filters.status">
            <option value="">{{ $t('imageBrowser.filters.statusAll') }}</option>
            <option v-for="s in statusKeys" :key="s" :value="s">
              {{ $t(`common.statuses.${s}`) }}
            </option>
          </select>
        </div>

        <div class="filter-field grow">
          <label :for="ids.search">{{ $t('imageBrowser.filters.searchLabel') }}</label>
          <input
            :id="ids.search"
            type="text"
            v-model="filters.search"
            :placeholder="$t('imageBrowser.filters.searchPlaceholder')"
          />
        </div>

        <div class="filter-field result-count">
          <div class="count-pill">{{ countText }}</div>
        </div>
      </div>

      <p v-if="loading" class="loading-row" role="status">{{ $t('imageBrowser.loading') }}</p>

      <div v-else class="image-grid">
        <div
          v-for="record in images"
          :key="record.id"
          class="image-card"
          @click="selectedImage = record"
        >
          <div class="thumb-wrap">
            <img
              :src="imageUrl(record)"
              :alt="altTextFor(record, currentLocale)"
              @error="onImgError($event, record, currentLocale)"
            />
          </div>
          <div class="card-meta">
            <div class="card-id">{{ formatCardId(record.id) }}</div>
            <div class="card-label" :title="labelOf(record)">{{ labelOf(record) }}</div>
            <span class="status-badge" :class="`status-${record.status}`">
              {{ $t(`common.statuses.${record.status}`) }}
            </span>
          </div>
        </div>
      </div>

      <ImageDetail
        v-if="selectedImage"
        :record="selectedImage"
        @close="selectedImage = null"
      />
    </div>
  </div>
</template>

<script>
import i18n from '@/i18n';
import { fetchImages } from '@/services/imageApi';
import {
  imageUrl,
  pickLocaleString,
  altTextFor,
  onImgError,
} from '@/services/imageHelpers';
import ImageDetail from '@/components/image_detail/ImageDetail.vue';

const STATUS_KEYS = ['DRAFT', 'PENDING', 'APPROVED', 'REJECTED', 'ARCHIVED'];
const CATEGORY_IDS = [1, 2, 3, 4, 5, 6, 7, 8, 9, 10];

let _uid = 0;
const nextId = () => `image-browser-field-${++_uid}`;

export default {
  name: 'ImageBrowser',
  components: { ImageDetail },
  data() {
    return {
      // TODO: replace with services/imageApi.js → fetchImages once filters
      // are implemented server-side; right now the response is the full library.
      allImages: [],
      loading: true,
      errorMessage: null,
      selectedImage: null,
      filters: {
        category: '',
        status: '',
        search: '',
      },
      categoryIds: CATEGORY_IDS,
      statusKeys: STATUS_KEYS,
      ids: {
        category: nextId(),
        status: nextId(),
        search: nextId(),
      },
    };
  },
  computed: {
    currentLocale() {
      return i18n.global.locale.value;
    },
    images() {
      const cat = this.filters.category;
      const status = this.filters.status;
      const q = this.filters.search.trim().toLowerCase();

      return this.allImages.filter(record => {
        if (cat !== '' && record.category_id !== Number(cat)) return false;
        if (status !== '' && record.status !== status) return false;
        if (q !== '') {
          const haystacks = [
            record.label_en, record.label_de, record.label_fr, record.label_it,
          ].filter(Boolean);
          const hit = haystacks.some(s => s.toLowerCase().includes(q));
          if (!hit) return false;
        }
        return true;
      });
    },
    countText() {
      const total = this.allImages.length;
      const shown = this.images.length;
      if (shown === total) {
        return this.$t('imageBrowser.filters.count', { n: shown });
      }
      return this.$t('imageBrowser.filters.countFiltered', { n: shown, total });
    },
  },
  async mounted() {
    await this.load();
  },
  methods: {
    imageUrl,
    altTextFor,
    onImgError,
    formatCardId(id) {
      return `#${String(id).padStart(4, '0')}`;
    },
    labelOf(record) {
      return pickLocaleString(record, 'label', this.currentLocale)
        || this.$t('imageBrowser.noLabel');
    },
    async load() {
      this.loading = true;
      this.errorMessage = null;
      try {
        this.allImages = await fetchImages();
      } catch (e) {
        this.errorMessage = this.$t('imageBrowser.error', { message: e?.message || String(e) });
        this.allImages = [];
      } finally {
        this.loading = false;
      }
    },
  },
};
</script>

<style scoped>
.image-browser-container {
  padding: 20px;
  font-family: 'Montserrat', 'Avenir', Helvetica, Arial, sans-serif;
}

.image-browser {
  max-width: 1400px;
  margin: 0 auto;
}

.green-heading {
  color: #42b983;
  font-weight: 600;
  margin: 0 0 0.25rem 0;
}

.subtitle {
  color: #6c757d;
  font-size: 14px;
  margin: 0 0 1.5rem 0;
}

/* --- Banners --- */

.banner {
  margin: 0 0 1rem 0;
  padding: 10px 14px;
  border-radius: 6px;
  font-size: 14px;
}
.banner-error { background: #F8D7DA; color: #721C24; border: 1px solid #f5c6cb; }

.loading-row {
  margin: 1rem 0;
  color: #6c757d;
  font-size: 14px;
  text-align: center;
}

/* --- Filter bar --- */

.filters {
  display: flex;
  flex-wrap: wrap;
  gap: 16px;
  align-items: flex-end;
  margin-bottom: 1.5rem;
}

.filter-field {
  display: flex;
  flex-direction: column;
  gap: 4px;
  min-width: 160px;
}

.filter-field.grow {
  flex: 1 1 240px;
  min-width: 240px;
}

.filter-field.result-count {
  min-width: auto;
}

.filter-field label {
  font-size: 12px;
  font-weight: 600;
  color: #6c757d;
  letter-spacing: 0.3px;
  text-transform: uppercase;
}

.filter-field select,
.filter-field input {
  font-family: inherit;
  font-size: 14px;
  padding: 8px 10px;
  border: 1px solid #ced4da;
  border-radius: 6px;
  background-color: #fff;
  color: #2c3e50;
  transition: border-color 0.15s ease, box-shadow 0.15s ease;
  height: 38px;
  box-sizing: border-box;
}

.filter-field select:focus,
.filter-field input:focus {
  outline: none;
  border-color: #42b983;
  box-shadow: 0 0 0 3px rgba(66, 185, 131, 0.18);
}

.count-pill {
  display: inline-flex;
  align-items: center;
  height: 38px;
  padding: 0 14px;
  border-radius: 19px;
  background-color: #f1f3f5;
  color: #2c3e50;
  font-size: 13px;
  font-weight: 500;
  white-space: nowrap;
}

/* --- Grid --- */

.image-grid {
  display: grid;
  grid-template-columns: repeat(auto-fill, minmax(180px, 1fr));
  gap: 16px;
}

.image-card {
  background: #fff;
  border: 1px solid #e9ecef;
  border-radius: 10px;
  overflow: hidden;
  display: flex;
  flex-direction: column;
  cursor: pointer;
  transition: box-shadow 0.15s ease, border-color 0.15s ease, transform 0.15s ease;
}

.image-card:hover {
  border-color: #42b983;
  box-shadow: 0 4px 12px rgba(0, 0, 0, 0.08);
}

.thumb-wrap {
  position: relative;
  width: 100%;
  aspect-ratio: 1 / 1;
  overflow: hidden;
  background: #f8f9fa;
}

.thumb-wrap img {
  width: 100%;
  height: 100%;
  object-fit: cover;
  display: block;
}

.card-meta {
  padding: 10px 12px 12px 12px;
  display: flex;
  flex-direction: column;
  gap: 4px;
}

.card-id {
  font-family: 'SFMono-Regular', Consolas, 'Liberation Mono', Menlo, monospace;
  font-size: 11px;
  color: #adb5bd;
  letter-spacing: 0.5px;
}

.card-label {
  font-size: 14px;
  font-weight: 500;
  color: #2c3e50;
  white-space: nowrap;
  overflow: hidden;
  text-overflow: ellipsis;
}

.status-badge {
  align-self: flex-start;
  padding: 3px 9px;
  border-radius: 12px;
  font-size: 11px;
  font-weight: 500;
  letter-spacing: 0.3px;
}

.status-DRAFT    { background: #E8E8E8; color: #555; }
.status-PENDING  { background: #FFF3CD; color: #856404; }
.status-APPROVED { background: #D4EDDA; color: #155724; }
.status-REJECTED { background: #F8D7DA; color: #721C24; }
.status-ARCHIVED { background: #E2E3E5; color: #383D41; }

/* --- Responsive --- */

@media (max-width: 1000px) {
  .image-grid {
    grid-template-columns: repeat(auto-fill, minmax(160px, 1fr));
  }
}

@media (max-width: 600px) {
  .image-grid {
    grid-template-columns: repeat(2, 1fr);
  }
  .filter-field,
  .filter-field.grow {
    flex: 1 1 100%;
    min-width: 0;
  }
}
</style>
